import axios, { AxiosError } from 'axios';
import { toast } from '@/app/utils/toast';
import { getAuthToken, reportUnauthorized } from '@/lib/auth-utils';

type ToastedAxiosError = AxiosError & { __apiErrorToasted?: boolean };

let installed = false;

const isPlainMessage = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const extractBackendMessage = (error: AxiosError): string | null => {
  const data = error.response?.data as
    | { message?: unknown; errors?: unknown }
    | undefined;

  if (!data) return null;

  const validationErrors = data.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const firstError = Object.values(validationErrors as Record<string, unknown>)[0];
    if (Array.isArray(firstError) && isPlainMessage(firstError[0])) {
      return firstError[0];
    }
  }

  if (isPlainMessage(data.message)) {
    return data.message;
  }

  return null;
};

export function setupGlobalAxiosErrorHandler(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error: ToastedAxiosError) => {
      const skip = (error?.config as { _skipGlobalErrorToast?: boolean } | undefined)
        ?._skipGlobalErrorToast === true;
      if (error?.response?.status === 401 && getAuthToken()) {
        reportUnauthorized();
        return Promise.reject(error);
      }
      if (error?.isAxiosError && error.response && !skip) {
        const status = error.response.status;
        if (status >= 400 && status < 500 && !error.__apiErrorToasted) {
          const message = extractBackendMessage(error);
          if (message) {
            error.__apiErrorToasted = true;
            toast.error(message, {
              position: 'bottom-left',
              autoClose: 5000,
              style: {
                width: '320px',
                fontSize: '14px',
                bottom: '10px',
              },
            });
          }
        }
      }
      return Promise.reject(error);
    }
  );
}
